import React from 'react';
import { cn } from '../../../utils/cn';

type Accent = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';

const ACCENT_VAR: Record<Accent, string> = {
  primary: 'var(--color-primary-500)',
  success: 'var(--color-success-500)',
  warning: 'var(--color-warning-500)',
  error: 'var(--color-error-500)',
  info: 'var(--color-info-500)',
  secondary: 'var(--color-secondary-500)',
};

export const Card: React.FC<{ children: React.ReactNode; className?: string; padded?: boolean }> = ({
  children,
  className,
  padded = true,
}) => (
  <div
    className={cn('rounded-xl border', padded && 'p-5', className)}
    style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
  >
    {children}
  </div>
);

export const SectionCard: React.FC<{
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}> = ({ title, description, actions, children, className, bodyClassName }) => (
  <div
    className={cn('rounded-xl border overflow-hidden', className)}
    style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
  >
    <div
      className="flex items-center justify-between gap-4 px-5 py-4 border-b"
      style={{ borderColor: 'var(--color-border-light)' }}
    >
      <div className="min-w-0">
        <h3 className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>
          {title}
        </h3>
        {description && (
          <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>}
    </div>
    <div className={cn('p-5', bodyClassName)}>{children}</div>
  </div>
);

export const PageHeader: React.FC<{
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  eyebrow?: string;
}> = ({ title, subtitle, actions, eyebrow }) => (
  <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div className="min-w-0">
      {eyebrow && (
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-primary-500)' }}>
          {eyebrow}
        </p>
      )}
      <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--color-text-primary)' }}>
        {title}
      </h1>
      {subtitle && (
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          {subtitle}
        </p>
      )}
    </div>
    {actions && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sublabel?: string;
  icon?: React.ReactNode;
  accent?: Accent;
}> = ({ label, value, sublabel, icon, accent = 'primary' }) => (
  <Card className="flex items-center gap-4">
    {icon && (
      <div
        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-lg"
        style={{ backgroundColor: `color-mix(in srgb, ${ACCENT_VAR[accent]} 14%, transparent)`, color: ACCENT_VAR[accent] }}
        aria-hidden="true"
      >
        {icon}
      </div>
    )}
    <div className="min-w-0">
      <p className="truncate text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
        {label}
      </p>
      <p className="text-2xl font-bold leading-tight" style={{ color: 'var(--color-text-primary)' }}>
        {value}
      </p>
      {sublabel && (
        <p className="truncate text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          {sublabel}
        </p>
      )}
    </div>
  </Card>
);

export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: string;
  message?: string;
  action?: React.ReactNode;
}> = ({ icon, title, message, action }) => (
  <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
    {icon && (
      <div
        className="mb-4 flex h-14 w-14 items-center justify-center rounded-full text-2xl"
        style={{ backgroundColor: 'var(--color-hover-background)', color: 'var(--color-text-secondary)' }}
        aria-hidden="true"
      >
        {icon}
      </div>
    )}
    <h3 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
      {title}
    </h3>
    {message && (
      <p className="mt-1 max-w-sm text-sm" style={{ color: 'var(--color-text-secondary)' }}>
        {message}
      </p>
    )}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export interface DescriptionItem {
  label: string;
  value: React.ReactNode;
}

export const DescriptionList: React.FC<{ items: DescriptionItem[]; columns?: 1 | 2 | 3 }> = ({
  items,
  columns = 2,
}) => (
  <dl
    className={cn(
      'grid gap-x-6 gap-y-4',
      columns === 1 && 'grid-cols-1',
      columns === 2 && 'grid-cols-1 sm:grid-cols-2',
      columns === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    )}
  >
    {items.map((item) => (
      <div key={item.label} className="min-w-0">
        <dt className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
          {item.label}
        </dt>
        <dd className="mt-1 text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
          {item.value}
        </dd>
      </div>
    ))}
  </dl>
);

export const ProgressBar: React.FC<{ value: number; accent?: Accent; className?: string }> = ({
  value,
  accent = 'primary',
  className,
}) => (
  <div
    className={cn('h-2 w-full overflow-hidden rounded-full', className)}
    style={{ backgroundColor: 'var(--color-hover-background)' }}
    role="progressbar"
    aria-valuenow={Math.round(value)}
    aria-valuemin={0}
    aria-valuemax={100}
  >
    <div
      className="h-full rounded-full transition-all"
      style={{ width: `${Math.min(100, Math.max(0, value))}%`, backgroundColor: ACCENT_VAR[accent] }}
    />
  </div>
);
